import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Button, Pager } from "rhombus-react";

import { buildSelectedAssetList } from "selectors/Assets";
import { Assets } from "reducers/Assets";
import { hasActiveFilters } from "reducers/common/ColumnFilters";
import { useCollectionFiltering } from "utils/DataGrid";
import { AssetSocketListener } from "hooks/useAssetSocket";
import useCancelToken from "hooks/useCancelToken";

import AssetToolbar from "./assets/Toolbar";
import AssetGrid    from "./assets/Grid";
import AssetModals  from "./assets/Modals";
import { fileUploadButtons } from "./assets/FileUploadActions";

import Permissioned from "widgets/Permissioned";
import EmptySection from "widgets/EmptySection";
import LinkedCreativeModal from "widgets/modals/assets/LinkedCreativeModal";
import UploadTextAssetModal from "widgets/modals/assets/UploadTextAssetModal";
import DynamicVariablesModal from "widgets/modals/assets/DynamicVariablesModal";
import NoAfterEffects from "widgets/modals/NoAfterEffects";
import FilterTagList from "widgets/toolbars/FilterTagList";
import { ASSET_COLUMNS } from "widgets/grid/columns/Assets";

import { CREATIVE_ROW_COUNT_OPTIONS } from "utils/constants";

import "./AssetList.css";

/*
   The distincts endpoint does not return distinct tags. Therefore, we handle it
   separately in the reducer.
*/
let distinctColumns = ASSET_COLUMNS
	.filter(v => /checkbox|range/gi.test(v.filter))
	.map(v => v.key)
	;

distinctColumns.push("assetType");


export default function AssetList({ libraryId, ...props }){
	const dispatch = useDispatch()
		, { assets,
			assetItems,
		} = useSelector((state) => {
			return {
				assets:state.assets,
				assetItems:buildSelectedAssetList(state),
			};
		});
	const { getNewToken } = useCancelToken(true);

	const {
		filterInfo: { page, rpp, searchTerm, sort },
		columnFilters,
	} = useCollectionFiltering(
		assets,
		Assets.setAdvancedFilter,
		() => {
			dispatch(Assets.clearAssets()).then(() => {
				dispatch(Assets.loadAssets(libraryId, getNewToken()));
			});
		}
	);


	useEffect(() => {
		dispatch(Assets.loadAssetDistincts(libraryId, distinctColumns));
	}, [libraryId]);

	function resetColumnFilters() {
		dispatch(Assets.clearColumnFilters());
	}

	function resetAllFilters() {
		resetColumnFilters();
		dispatch(Assets.setSearchTerm(""));
	}

	let hasActive = hasActiveFilters(assets)
		, hasFiltersAppliedClass = hasActive ? "has-filters-applied" : ""
		, screenPath = props?.match?.url
		, returnPath = `${screenPath}/`
		, $modals = (
			<AssetModals
				key="modals"
				screenPath = {screenPath}
				returnPath = {returnPath}
				libraryId = {libraryId}
			/>
		)
		, $toolbar = (
			<AssetToolbar
				key="toolbar"
				libraryId={libraryId}
				searchTerm = {searchTerm}
				handleSearch = {newSearchTerm => dispatch(Assets.setSearchTerm(newSearchTerm))}
				isLoading = {!assets.isLoaded}
			/>
		)
		, $filterList = (
			<FilterTagList
				key="filter-tags"
				Reducer = {Assets}
				filters = {columnFilters}
				resetFilters={resetColumnFilters}
				excludedTagNames = {["assetType"]} // assetType filtering is already represented by a ToggleFilter
			/>
		)
		, displayWhat = [
			$modals,
			<LinkedCreativeModal key="linked-creatives" />,
			<DynamicVariablesModal key="dynamic-variables" />,
			<UploadTextAssetModal key="upload-text-asset" />,
			<NoAfterEffects key="no-after-effects" />,
		]
		;

	const pagerProps = {
		showItemCount: true,
		initialPage: page,
		forcePage: page,
		pageSize: rpp,
		pageCount: assets.totalPages,
		maxItems: assets.totalItems,
		showItemWord: "",
		rowCountItems: CREATIVE_ROW_COUNT_OPTIONS,

		disabled: !assets.isLoaded,

		onPageChange: newPage => dispatch(Assets.setPage(newPage)),
		onRowCountChange: newRPP => dispatch(Assets.setRPP(newRPP)),
	};

	if(assets.isLoaded && assets.items.length === 0) {
		let emptyProps = null;

		if(hasActive || searchTerm){
			displayWhat = displayWhat.concat([$toolbar, $filterList]);
		}

		if(hasActive){
			emptyProps = {
				className: "empty-assets no-filter-results",
				header: "No results to display",
				message: "Try adjusting your filters to find what you are looking for",
				src: "/images/noFiles.svg",
				buttons:[
					<Button key="clear" onClick={resetAllFilters}>Clear All Filters</Button>
				],
			};
		} else if(searchTerm){
			emptyProps = {
				className: "empty-assets no-search-results",
				header: "No search results to display",
				message: "Try adjusting your search to find what you are looking for",
				src: "/images/noSearch.svg",
				buttons:[
					<Button key="clear" onClick={() => dispatch(Assets.setSearchTerm(""))}>Clear Search</Button>
				],
			};
		} else if(assets.hasErrorLoading) {
			emptyProps = {
				key: "error-item",
				header: "There was a Problem Loading Assets",
				icon: "fas fa-bug",
				buttons: [
					<Button key="reload" icon="refresh"
						onClick={resetAllFilters}>
						Re-Try
					</Button>
				],
			};
		} else {
			let noAssetButtons = (
				<Permissioned key="upload-items" domain="Creative View" action="Upload Assets">
					{fileUploadButtons}
				</Permissioned>
			);

			emptyProps = {
				className: "empty-assets",
				header: "No assets have been added",
				message: "Start by selecting from the following options",
				src: "/images/noFiles.svg",
				buttons: noAssetButtons,
			};
		} //end if(compiledFilters)

		displayWhat = displayWhat.concat(<AssetSocketListener><EmptySection key="empty" {...emptyProps} /></AssetSocketListener>);
	} else {
		displayWhat = displayWhat.concat([
			$toolbar, $filterList
			, <AssetGrid
				key="asset-grid"
				assets = {assetItems}
				isLoading={!assets.isLoaded}
				sort={sort} // Needed to display the initial column sort order indicator
			/>
			, <Pager key="pager" {...pagerProps} />
		]);
	}	//end if(assets.isLoaded)

	return <div className={`asset-list ${hasFiltersAppliedClass}`}>{displayWhat}</div>;
}

AssetList.displayName = "AssetList";
